import { useState, useEffect, useCallback, useRef } from 'react'
import { normalizeRoles, type User } from '../../data/users'
import { getMe } from '../../api/auth/getMe'
import { getToken } from '../../api/auth/getToken'
import { destroyToken } from '../../api/auth/destroyToken'
import { AuthContext } from './definition'
// import type { OneLoginSession } from './definition'
import { getRoles } from '@/api/roles/getRoles'
import { normalizePermissionName, type Role } from '@/data'

export { AuthContext } from './definition'
export type { AuthContextType, OneLoginSession } from './definition'

async function getAllRoles() {
  let page = 1
  let lastPage = 1
  const roles: Role[] = []

  do {
    const response = await getRoles(page)
    roles.push(...response.data)
    lastPage = response.meta.last_page
    page += 1
  } while (page <= lastPage)

  return roles
}

async function withResolvedPermissions(user: User): Promise<User> {
  if (user.roleIds.length === 0) {
    return user
  }

  const roles = await getAllRoles()
  const rolePermissions = roles
    .filter((role) => user.roleIds.includes(role.id))
    .flatMap((role) => role.permissions ?? [])

  return {
    ...user,
    permissions: Array.from(
      new Set([
        ...user.permissions,
        ...rolePermissions.map((permission) =>
          normalizePermissionName(permission.name),
        ),
      ]),
    ),
    permissionIds: Array.from(
      new Set([
        ...user.permissionIds,
        ...rolePermissions.map((permission) => permission.id),
      ]),
    ),
  }
}

function withRole(user: User): User {
  const roles = normalizeRoles(user)

  if (roles.length > 0) {
    return { ...user, role: user.role ?? roles[0], roles }
  }

  // TODO: remove when backend returns roles consistently
  const fallbackRole = user.email === 'test@turbodebt.com' ? 'admin' : 'user'
  return { ...user, role: fallbackRole, roles: [fallbackRole] }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const hasToken = Boolean(localStorage.getItem('td-credential'))
  const [loading, setLoading] = useState(hasToken)

  const hasFetchedAuth = useRef(false)

  useEffect(() => {
    if (!hasToken || hasFetchedAuth.current) return
    hasFetchedAuth.current = true
    getMe()
      .then(withResolvedPermissions)
      .then((userData) => setUser(withRole(userData)))
      .catch(() => {
        localStorage.removeItem('td-credential')
      })
      .finally(() => setLoading(false))
  }, [hasToken])

  const login = useCallback(async () => {
    // Token already stored in localStorage by the Login page (PKCE flow)
    setLoading(true)
    hasFetchedAuth.current = true
    try {
      const userData = await getMe().then(withResolvedPermissions)
      setUser(withRole(userData))
    } catch {
      localStorage.removeItem('td-credential')
    } finally {
      setLoading(false)
    }
  }, [])

  const passwordLogin = useCallback(async (email: string, password: string) => {
    const { user } = await getToken({ email, password })
    const resolvedUser = await withResolvedPermissions(user)
    setUser(withRole(resolvedUser))
  }, [])

  const logout = useCallback(async () => {
    try {
      await destroyToken()
    } catch {
      localStorage.removeItem('td-credential')
    }
    hasFetchedAuth.current = false
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, login, passwordLogin, logout, loading }}
    >
      {children}
    </AuthContext.Provider>
  )
}
